import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { baseUrlApi } from "../../proxy";

type PageBanner = {
  first_title?: string | false;
  second_title?: string | false;
  image_1920?: string | false;
  url?: string | false;
};

const bannerCache = new Map<string, PageBanner | null>();

/** Last-resort banner: used whenever the API and the caller give nothing. */
export const DEFAULT_PAGE_BANNER = "/assets/imgs/page-header/banner.png";

const publicPages = new Set([
  "about", "our-branches", "our-partners", "offers", "faq", "services",
  "products", "news", "jobs", "contact-us", "warranty",
  "privacy-policy", "terms-conditions", "return-policy", "loyalty-program",
]);

function firstBanner(value: unknown): PageBanner | null {
  if (Array.isArray(value)) {
    for (const entry of value) {
      const banner = firstBanner(entry);
      if (banner) return banner;
    }
    return null;
  }
  if (!value || typeof value !== "object") return null;
  const item = value as Record<string, unknown>;
  if (["first_title", "second_title", "image_1920"].some(
    (key) => typeof item[key] === "string" && (item[key] as string).trim()
  ))
    return item as PageBanner;
  for (const key of ["result", "data", "banners"]) {
    if (key in item) {
      const found = firstBanner(item[key]);
      if (found) return found;
    }
  }
  return null;
}

export function usePageBanner() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const page = pathname.split("/").filter(Boolean)[0]?.toLowerCase();
  const requestKey = `${page}:${i18n.language}`;
  const [result, setResult] = useState<{
    key: string;
    banner: PageBanner | null;
  } | null>(null);
  const enabled = Boolean(page && publicPages.has(page));

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    const cached = bannerCache.get(requestKey);
    if (bannerCache.has(requestKey)) {
      setResult({ key: requestKey, banner: cached ?? null });
    }
    axios.get(`${baseUrlApi}portal/banners`, {
      params: { page, lang: i18n.language },
      signal: controller.signal,
    }).then((response) => {
      const banner = firstBanner(response.data);
      bannerCache.set(requestKey, banner);
      setResult({ key: requestKey, banner });
    })
      .catch((error) => {
        if (!axios.isCancel(error) && !bannerCache.has(requestKey)) {
          setResult({ key: requestKey, banner: null });
        }
      });
    return () => controller.abort();
  }, [page, i18n.language, requestKey, enabled]);

  return {
    banner: enabled && result?.key === requestKey
      ? result.banner
      : bannerCache.get(requestKey) ?? null,
    loading: enabled && result?.key !== requestKey && !bannerCache.has(requestKey),
  };
}

export function pageBannerImage(image: string | false | undefined): string | null {
  if (typeof image !== "string" || !image.trim()) return null;
  const value = image.trim();
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("//")) return `${window.location.protocol}${value}`;
  return new URL(value, `${baseUrlApi}`).href;
}

export function pageBannerUrl(url: string | false | undefined): string | null {
  if (typeof url !== "string" || !url.trim()) return null;
  const value = url.trim();
  if (value.startsWith("/") && !value.startsWith("//")) return value;
  if (/^https?:\/\//i.test(value)) return value;
  return null;
}
